// src/pages/Blog.js
import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowUpRight } from 'react-icons/fi';
import BlogHero from '../components/BlogHero';
import BlogCard from '../components/BlogCard';
import NewsletterSignup from '../components/NewsletterSignup';
import SEO from '../components/SEO';
import { blogPosts } from '../data/blogPosts';

const Blog = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const isPublished = (p) => (p.status || '').toLowerCase() === 'published';
  const publishedPosts = blogPosts.filter(isPublished);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-off-white"
    >
      <SEO
        title="Insights"
        description="Perspectives on AI, PropTech, buyer behaviour, product building and the infrastructure behind modern real estate sales."
        path="/insights"
      />

      <BlogHero />

      <section className="bg-white border-b border-gray-100">
        <div className="container-custom px-6 py-14 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8">
              <p className="text-sm uppercase tracking-[0.2em] text-gold font-semibold mb-3">
                Selected Writing
              </p>

              <h2 className="text-3xl md:text-4xl font-serif text-navy mb-3">
                Essays published beyond this site.
              </h2>

              <p className="text-charcoal max-w-2xl leading-relaxed">
                Selected independent writing on AI, product thinking and the evolution of real estate technology.
              </p>
            </div>

            <a
              href="https://medium.com/@anu.jain328/why-im-building-empathy-first-ai-for-real-estate-ada13a13e115"
              target="_blank"
              rel="noopener noreferrer"
              className="group block border border-gray-200 rounded-2xl p-6 md:p-8 hover:border-gold/60 hover:shadow-md transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="max-w-3xl">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gold">
                      Medium
                    </span>

                    <span className="text-sm text-charcoal/60">
                      July 1, 2025
                    </span>

                    <span className="text-sm text-charcoal/60">
                      5 min read
                    </span>
                  </div>

                  <h3 className="text-2xl md:text-3xl font-serif text-navy mb-3 group-hover:text-gold transition-colors">
                    Why I'm Building Empathy-First AI for Real Estate
                  </h3>

                  <p className="text-charcoal leading-relaxed">
                    An earlier perspective on the ideas that shaped my approach to building AI for real estate, written as Zakool was taking shape.
                  </p>
                </div>

                <div className="flex items-center gap-2 text-navy font-medium whitespace-nowrap">
                  Read on Medium
                  <FiArrowUpRight className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      <div className="container-custom section-padding">
        <div className="mb-10">
          <p className="text-sm uppercase tracking-[0.2em] text-gold font-semibold mb-3">
            From AnuSingh.ai
          </p>

          <h2 className="text-3xl md:text-4xl font-serif text-navy">
            Latest Insights
          </h2>
        </div>

        {publishedPosts.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {publishedPosts.map((post) => (
              <motion.div key={post.id} variants={itemVariants}>
                <BlogCard post={post} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-16"
          >
            <p className="text-charcoal text-lg">
              More insights coming soon. Building in public, one story at a time.
            </p>
          </motion.div>
        )}
      </div>

      <div className="bg-white py-16">
        <NewsletterSignup />
      </div>
    </motion.div>
  );
};

export default Blog;